import { create } from 'zustand';

import { storage } from '../lib/storage.js';

const ACCESS_KEY = 'sitewise:access-token';
const REFRESH_KEY = 'sitewise:refresh-token';
const KIND_KEY = 'sitewise:auth-kind';

export type AuthKind = 'org' | 'admin' | 'vendor';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  /** Distinguishes org-scoped sessions from super-admin sessions. */
  kind: AuthKind | null;

  setTokens(args: {
    accessToken: string;
    refreshToken: string;
    kind: AuthKind;
  }): void;
  clear(): void;
  hydrateFromStorage(): void;
}

/**
 * Reads tokens off localStorage at module load. Doing this synchronously
 * is essential: React Router's data-router runs route loaders BEFORE the
 * provider tree mounts, so a `useEffect`-based hydration is too late for
 * `requireAdminAuth` / `requireOrgAuth` and authed URLs would bounce to
 * the login page on every cold navigation (refresh, bookmark, deep
 * link).
 */
function readPersistedAuth(): {
  accessToken: string | null;
  refreshToken: string | null;
  kind: AuthKind | null;
} {
  const accessToken = storage.get(ACCESS_KEY);
  const refreshToken = storage.get(REFRESH_KEY);
  const kindValue = storage.get(KIND_KEY);
  const kind =
    kindValue === 'org' || kindValue === 'admin' || kindValue === 'vendor' ? kindValue : null;
  return { accessToken, refreshToken, kind };
}

/**
 * Central token store. Persisted to localStorage so refreshes survive
 * page reloads. Auth-related side effects (HTTP, redirects) live in the
 * API client and route loaders, not in this store.
 */
export const useAuthStore = create<AuthState>((set) => ({
  ...readPersistedAuth(),

  setTokens: ({ accessToken, refreshToken, kind }) => {
    storage.set(ACCESS_KEY, accessToken);
    storage.set(REFRESH_KEY, refreshToken);
    storage.set(KIND_KEY, kind);
    set({ accessToken, refreshToken, kind });
  },

  clear: () => {
    storage.remove(ACCESS_KEY);
    storage.remove(REFRESH_KEY);
    storage.remove(KIND_KEY);
    set({ accessToken: null, refreshToken: null, kind: null });
  },

  hydrateFromStorage: () => {
    set(readPersistedAuth());
  },
}));

export function isAuthenticated(): boolean {
  const state = useAuthStore.getState();
  return Boolean(state.accessToken && state.refreshToken);
}
